import React, { useRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Intent, MenuItem } from '@blueprintjs/core';
import classNames from 'classnames';
import { Button } from 'components/buttons';
import { GridLayout } from 'components/grid';
import { FormNumericInput, FormSwitch, FormTextArea, FormTextInput, FormDateInput, FormSelect, FormHelperText, FormCheckbox, FormRadio, FormReadonly, FormSuggest } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { useValidation } from 'hooks/useValidation';
import { QuestionTypesConfiguration } from 'components/hierarchy/QuestionTypesConfiguration';
import moment from 'moment';
import { Tooltip } from 'components/tooltip';
import { HierarchyRepeatableModal } from 'components/hierarchy/HierarchyRepeatableModal';
import { HierarchyConditionalModal } from 'components/hierarchy/HierarchyConditionalModal';
import { HierarchyConditionsHelper } from 'components/hierarchy/HierarchyConditionsHelper';
import { HierarchyQuestionOptions } from 'components/hierarchy/HierarchyQuestionOptions';
import { ListingTable } from 'components/listing';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { AlertConfirm } from 'components/notifications';
import { useDirtyData } from 'hooks/useDirtyData';
import { useTemplateContext } from 'pages/checks/context/';
import { MediaList } from '../media';
import { Dropdown } from 'components/dropdown';
import { HierarchyAddQuestionModal } from 'components/hierarchy/HierarchyAddQuestionModal';
import './Hierarchy.css';

export function HierarchyQuestion({
    settings,
    loading,
    disabled,
    onSave,
    onEdit,
    onClone,
    onDelete,
    onClear,
    onAddMedia,
    questionNumber,
    reasonWhyComponentCannotBeRepeatable,
    questionTypes,
    inEditMode,
    pottentialTriggerQuestions,
    currentTriggerQuestion,
    conditionalHelperText,
    helperImages,
    conditionsWhereThisIsTheTrigger,
    onAddQuestion,
    onAddSection,
    onAddPage,
    readOnly,
    isItemBeingMoved }) {
    const [questionSettings, setQuestionSettings] = useState({});
    const [addingQuestion, setAddingQuestion] = useState(false);
    const [oldQuestionSettings, setOldQuestionSettings] = useState({});
    const [expanded, setExpanded] = useState(true);
    const [wasExpandedBefore, setWasExpandedBefore] = useState(true);
    const [isValid, errors, validate] = useValidation();
    const [settingRepeatability, setSettingRepeatability] = useState(false);
    const [settingConditionality, setSettingConditionality] = useState(false);
    const [customSettingIsValid, setCustomSettingIsValid] = useState(true);
    const [showClearSettingsWarning, setShowClearSettingsWarning] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [initialHelperImages, setInitialHelperImages] = useState(helperImages || []);
    const [draftHelperImages, setDraftHelperImages] = useState([]);
    const { registerMediaAdded, registerMediaDeleted } = useTemplateContext();
    const hasInitialized = useRef(false);

    const selectedQuestionType = useMemo(() => {
        return questionTypes.find(x => settings.$componentType.startsWith(x.key));
    }, [questionTypes, settings]);

    useEffect(validateQuestion, [questionSettings]);

    const isDataDirty = useDirtyData(oldQuestionSettings, questionSettings);

    const triggerQuestion = useMemo(() => HierarchyConditionsHelper.getTriggerQuestion(questionSettings.condition, pottentialTriggerQuestions)
        , [questionSettings.condition?.triggerQuestionId, pottentialTriggerQuestions]);

    const triggerAnswers = useMemo(() => HierarchyConditionsHelper.getConcatenatedAnswers(triggerQuestion, questionSettings.condition),
        [triggerQuestion, questionSettings.condition, pottentialTriggerQuestions]);

    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: `question-${settings.id}`,
        disabled: readOnly
    });

    const draggableStyle = {
        transform: CSS.Translate.toString(transform),
    }

    function validateQuestion() {
        let rules = [];
        let externalRules = [];

        rules.push({ fieldName: "title", required: true });

        const configuration = QuestionTypesConfiguration[settings.$componentType];
        if (configuration) {
            for (const field of configuration.fields) {
                const rule = { fieldName: field.key };
                if (typeof field.validationRule === 'function') {
                    field.validationRule(questionSettings, rule);
                }

                if (field.validationRules) {
                    field.validationRules(questionSettings, rules);
                }

                rules.push(rule);
            }
        }

        // Add custom validation for Date questions using external rules
        if (settings.$componentType === "DateQuestion") {
            // Check if both preventPastDates and preventFutureDates are enabled
            if (questionSettings.preventPastDates && questionSettings.preventFutureDates) {
                externalRules.push({
                    fieldName: "preventPastDates",
                    valid: false,
                    errorMessage: "Cannot exclude both past and future dates - this would make no dates selectable"
                });
                externalRules.push({
                    fieldName: "preventFutureDates",
                    valid: false,
                    errorMessage: "Cannot exclude both past and future dates - this would make no dates selectable"
                });
            }
        }

        // Add custom validation for DefectCapture and MediaCapture questions using external rules
        if (settings.$componentType === "DefectCaptureQuestion" || settings.$componentType === "MediaCaptureQuestion") {
            // Check if minNumberOfMedia is greater than or equal to maxNumberOfMedia
            if (questionSettings.minNumberOfMedia && questionSettings.maxNumberOfMedia &&
                questionSettings.minNumberOfMedia >= questionSettings.maxNumberOfMedia) {
                externalRules.push({
                    fieldName: "minNumberOfMedia",
                    valid: false,
                    errorMessage: "Minimum number of uploads must be less than maximum number of uploads"
                });
                externalRules.push({
                    fieldName: "maxNumberOfMedia",
                    valid: false,
                    errorMessage: "Maximum number of uploads must be greater than minimum number of uploads"
                });
            }
        }

        // Add custom validation for MediaCapture questions using external rules
        if (settings.$componentType === "MediaCaptureQuestion") {
            // Check if at least one media type is enabled
            if (!questionSettings.allowImages && !questionSettings.allowVideos) {
                externalRules.push({
                    fieldName: "allowImages",
                    valid: false,
                    errorMessage: "At least one media type (images or videos) must be enabled"
                });
                externalRules.push({
                    fieldName: "allowVideos",
                    valid: false,
                    errorMessage: "At least one media type (images or videos) must be enabled"
                });
            }
        }

        // Add custom validation for Text and TextArea questions using external rules
        if (settings.$componentType === "TextQuestion" || settings.$componentType === "TextAreaQuestion") {
            const minLength = questionSettings.minLength;
            const maxLength = questionSettings.maxLength;

            // Check for negative length values
            if (minLength < 0) {
                externalRules.push({
                    fieldName: "minLength",
                    valid: false,
                    errorMessage: "Minimum length cannot be negative"
                });
            }
            if (maxLength < 0) {
                externalRules.push({
                    fieldName: "maxLength",
                    valid: false,
                    errorMessage: "Maximum length cannot be negative"
                });
            }
            // Check if both min and max length are 0 (only if neither is negative)
            if (minLength >= 0 && maxLength >= 0 && minLength === 0 && maxLength === 0) {
                externalRules.push({
                    fieldName: "minLength",
                    valid: false,
                    errorMessage: "Cannot set both minimum and maximum length to 0 - this would make the field unusable"
                });
                externalRules.push({
                    fieldName: "maxLength",
                    valid: false,
                    errorMessage: "Cannot set both minimum and maximum length to 0 - this would make the field unusable"
                });
            }
            // Check if minimum length is greater than maximum length (only if both are non-negative)
            if (minLength >= 0 && maxLength >= 0 && minLength > 0 && minLength > maxLength) {
                externalRules.push({
                    fieldName: "minLength",
                    valid: false,
                    errorMessage: "Minimum length cannot be greater than maximum length"
                });
                externalRules.push({
                    fieldName: "maxLength",
                    valid: false,
                    errorMessage: "Maximum length cannot be less than minimum length"
                });
            }
        }

        validate(rules, questionSettings, externalRules);
    }

    useEffect(function () {
        setIsNew(!settings.title);
        applyDefaultsToSettings();
    }, [settings]);

    useEffect(() => {
        if (isItemBeingMoved) {
            setWasExpandedBefore(expanded);
            setExpanded(false);
        } else {
            setExpanded(wasExpandedBefore);
        }
    }, [isItemBeingMoved]);

    useEffect(() => {
        if (inEditMode) {
            setExpanded(true);
        }
    }, [inEditMode]);

    useEffect(() => {
        if (inEditMode && !hasInitialized.current) {
            setInitialHelperImages(helperImages || []);
            setDraftHelperImages([]);
            hasInitialized.current = true;
        }
    }, [inEditMode, helperImages]);


    const applyDefaultsToSettings = () => {
        const configuration = QuestionTypesConfiguration[settings.$componentType];
        const fields = configuration?.fields ?? [];
        const newSettings = settings ? { ...settings } : {};

        for (const field of fields) {
            if (field.default != null && newSettings[field.key] == null) {
                newSettings[field.key] = field.default;
            }

            if (field.defaults) {
                for (let fieldDefault of field.defaults) {
                    newSettings[fieldDefault.key] =
                        newSettings[fieldDefault.key] ?? fieldDefault.value;
                }
            }
        }

        if (selectedQuestionType.isRequiredByDefault
            && newSettings.required == null
        ) {
            newSettings.required = true;
        }

        setQuestionSettings(newSettings);
        setOldQuestionSettings({ ...newSettings });
    }

    function handleSave() {
        const allImages = [...initialHelperImages, ...draftHelperImages];

        registerMediaAdded(
            draftHelperImages.map(img => img.mediaReference)
        );

        onSave(questionSettings);

        setInitialHelperImages(allImages);
        setDraftHelperImages([]);
    }

    function handleEdit() {
        setExpanded(true);
        onEdit(questionSettings.id);
    }

    function handleClone() {
        onClone(questionSettings.id);
    }

    function handleDelete() {
        onDelete(questionSettings.id);
    }

    function handleClear() {
        if (isDataDirty) {
            setShowClearSettingsWarning(true);
        } else {
            onClear();
        }
    }

    function onClearConfirmed() {
        setShowClearSettingsWarning(false);
        onClear();
        setDraftHelperImages([]);
        setInitialHelperImages(helperImages)
    }

    function onTitleChange(event) {
        const newTitle = event.target.value;
        setQuestionSettings(function (prev) {
            return { ...prev, title: newTitle };
        });
    }

    function onHelperTextChange(event) {
        const newText = event.target.value;
        setQuestionSettings(function (prev) {
            return { ...prev, helperText: newText };
        });
    }

    function onSettingsChange(newValue, fieldKey) {
        const clone = { ...questionSettings };
        clone[fieldKey] = newValue;
        setQuestionSettings(clone);
    }

    function handleToggle() {
        setExpanded(!expanded);
    }

    function handleRequiredToggle() {
        setQuestionSettings(function (prev) {
            return { ...prev, required: !prev.required };
        });
    }

    const handleRepeatableToggle = () => {
        setSettingRepeatability(true);
    };

    const handleConditionalToggle = () => {
        setSettingConditionality(true);
    };

    const onRepeatabilityChange = (newValue) => {
        setQuestionSettings({
            ...questionSettings,
            repeatability: newValue ? { ...newValue } : null
        });
        setSettingRepeatability(false);
    };

    const onConditionalityChange = (newValue) => {
        setQuestionSettings({
            ...questionSettings,
            condition: newValue ? { ...newValue } : null
        });
        setSettingConditionality(false);
    };

    const renderQuestionSettings = () => {
        const questionSettingsConfiguration = QuestionTypesConfiguration[settings.$componentType];

        if (questionSettingsConfiguration) {
            return questionSettingsConfiguration.fields.map(c => renderSetting(c));
        }

        return <span className='bold'>Question type not implemented</span>;
    }

    const onNumberSettingChange = (value, settingConfiguration) => {
        const min = settingConfiguration.min(questionSettings);
        const max = settingConfiguration.max(questionSettings);

        if (value < min) {
            value = min;
        } else if (value > max) {
            value = max;
        }

        onSettingsChange(value, settingConfiguration.key);
    }

    const getNumberSettingMin = (settingConfiguration) => {
        const min = settingConfiguration.min(questionSettings);

        return isNaN(min) ? null : min;
    }

    const getNumberSettingMax = (settingConfiguration) => {
        const max = settingConfiguration.max(questionSettings);

        return isNaN(max) ? null : max;
    }

    const handleAddMedia = async (file) => {
        const guid = crypto.randomUUID();

        const newMedia = await onAddMedia(guid, file);

        if (!newMedia) {
            return;
        }

        setDraftHelperImages(prev => {
            const updatedDrafts = [...prev, newMedia];

            const combinedRefs = [...initialHelperImages, ...updatedDrafts].map(m => m.mediaReference);

            setQuestionSettings(prevSettings => ({
                ...prevSettings,
                helperImages: combinedRefs
            }));

            return updatedDrafts;
        });
    };

    const handleDeleteMedia = (file) => {
        const updatedDrafts = draftHelperImages.filter(
            media => media.mediaReference !== file.mediaReference
        );

        const updatedOriginals = initialHelperImages.filter(
            media => media.mediaReference !== file.mediaReference
        );

        setDraftHelperImages(updatedDrafts);
        setInitialHelperImages(updatedOriginals);

        const combinedRefs = [...updatedOriginals, ...updatedDrafts].map(m => m.mediaReference);
        setQuestionSettings(prevSettings => ({
            ...prevSettings,
            helperImages: combinedRefs
        }));

        registerMediaDeleted([file.mediaReference]);
    };

    const renderSetting = (settingConfiguration) => {
        switch (settingConfiguration.$type) {
            case "Number":
                return <FormNumericInput
                    key={settingConfiguration.key}
                    min={getNumberSettingMin(settingConfiguration)}
                    max={getNumberSettingMax(settingConfiguration)}
                    selectedValue={questionSettings[settingConfiguration.key]}
                    disabled={readOnly}
                    disabledInput={readOnly}
                    onValueChange={(value) => onNumberSettingChange(value, settingConfiguration)}
                    headingText={settingConfiguration.title}
                    dangerHelperText={errors[settingConfiguration.key]}
                    allowDecimal={settingConfiguration.numberOfDecimalPlaces(questionSettings) > 0}
                    decimalPlaces={settingConfiguration.numberOfDecimalPlaces(questionSettings)}
                ></FormNumericInput>
            case "Switch":
                return <FormSwitch
                    key={settingConfiguration.key}
                    headingText={settingConfiguration.title}
                    checked={questionSettings[settingConfiguration.key]}
                    disabled={readOnly}
                    onChange={(event) => onSettingsChange(event.target.checked, settingConfiguration.key)}
                    dangerHelperText={errors[settingConfiguration.key]}
                />
            case "Options":
                return <HierarchyQuestionOptions
                    key={settingConfiguration.key}
                    options={questionSettings.options}
                    onChange={(value) => onSettingsChange(value, settingConfiguration.key)}
                    multipleCanBeSelectedByDefault={settingConfiguration.multipleCanBeSelectedByDefault}
                    onValidChange={(optionIsValid) => setCustomSettingIsValid(optionIsValid)}
                    header={settingConfiguration.title}
                    readOnly={readOnly}
                    conditionsWhereThisIsTheTrigger={conditionsWhereThisIsTheTrigger}
                />
            case "BinarySelect":
                return (
                    <div className="form-field spacer-bottom-small">
                        <ListingTable
                            data={[
                                [
                                    readOnly ? <span key="name">{questionSettings.labelForYes}</span> : <FormTextInput
                                        key="name"
                                        value={questionSettings.labelForYes}
                                        placeholder="Label for yes"
                                        onChange={(e) =>
                                            onSettingsChange(e.target.value, "labelForYes")
                                        }
                                    />,
                                    <FormCheckbox
                                        key="default"
                                        checked={questionSettings.defaultValue === true}
                                        onChange={(e) =>
                                            onSettingsChange(
                                                questionSettings.defaultValue ? null : true,
                                                "defaultValue"
                                            )
                                        }
                                        disabled={readOnly}
                                    />,
                                ],
                                [
                                    readOnly ? <span key="name">{questionSettings.labelForNo}</span> : <FormTextInput
                                        key="name"
                                        value={questionSettings.labelForNo}
                                        placeholder="Label for no"
                                        onChange={(e) =>
                                            onSettingsChange(e.target.value, "labelForNo")
                                        }
                                    />,
                                    <FormCheckbox
                                        key="default"
                                        checked={questionSettings.defaultValue === false}
                                        onChange={(e) =>
                                            onSettingsChange(
                                                questionSettings.defaultValue === false ? null : false,
                                                "defaultValue"
                                            )
                                        }
                                        disabled={readOnly}
                                    />,
                                ],
                            ]}
                            pageable={false}
                            headers={["Name", "Default"]}
                            loadingData={false}
                            fitToContent={false}
                            totalRecordCount={2}
                            shrinkLastColumn={true}
                        />
                        <ShowHide
                            evaluator={!!errors["labelForYes"] || !!errors["labelForNo"]}
                            show={
                                <FormHelperText danger={true}>
                                    The names for the two options are required
                                </FormHelperText>
                            }
                        />
                    </div>
                );
            case "Text":
                return <FormTextInput
                    key={settingConfiguration.key}
                    value={questionSettings[settingConfiguration.key] || ""}
                    onChange={(event) => onSettingsChange(event.target.value, settingConfiguration.key)}
                    headingText={settingConfiguration.title}
                    helperText={settingConfiguration.helperText}
                    dangerHelperText={errors[settingConfiguration.key]}
                    disabled={readOnly}
                    fill
                />
            case "Suggest":
                return <FormSuggest
                    key={settingConfiguration.key}
                    selectedValue={questionSettings[settingConfiguration.key] || ""}
                    items={settingConfiguration.items}
                    onFilter={selectedQuestionType.onFilter}
                    onItemSelect={item => {
                        onSettingsChange(item.name, settingConfiguration.key)
                    }}
                    headingText={settingConfiguration.title}
                    helperText={settingConfiguration.helperText}
                    dangerHelperText={errors[settingConfiguration.key]}
                    getDynamicItems={settingConfiguration.getDynamicItems}
                    disabled={readOnly}
                    allowCreateItem={true}
                />
            default:
                return <span>Setting {settingConfiguration.$type} not implemented</span>
        }
    }

    const renderCameraButton = () => (
        <button
            className="camera-button"
            disabled={true}
        >
            <Icon icon="camera" />
        </button>
    );

    const renderQuestionDisplay = () => {
        const displayType = QuestionTypesConfiguration[settings.$componentType]?.displayType;

        switch (displayType) {
            case "Text":
                return <FormTextInput
                    helperText={questionSettings.helperText}
                    value={''}
                    onChange={() => null}
                    headingText={questionSettings.title}
                    disabled={true}
                    fill
                ></FormTextInput>
            case "Number":
                return <FormNumericInput
                    helperText={questionSettings.helperText}
                    selectedValue={0}
                    onValueChange={() => null}
                    headingText={questionSettings.title}
                    disabled={true}
                    disabledInput={true}
                ></FormNumericInput>
            case "TextArea":
                return <FormTextArea
                    helperText={questionSettings.helperText}
                    value={''}
                    onChange={() => null}
                    headingText={questionSettings.title}
                    disabled={true}
                ></FormTextArea>
            case "Date": {
                const defaultDate = questionSettings.defaultToTodaysDate ? moment().format() : null;
                return <FormDateInput
                    helperText={questionSettings.helperText}
                    value={defaultDate}
                    onChange={(value) => onSettingsChange(value ? moment(value).format("YYYY-MM-DD") : null, 'answer')}
                    headingText={questionSettings.title}
                    disabled={!inEditMode}
                    includeTime={questionSettings.includeTime}
                    minDate={questionSettings.preventPastDates ? moment().toDate() : null}
                    maxDate={questionSettings.preventFutureDates ? moment().toDate() : null}
                />
            }
            case "Dropdown":
                return <FormSelect
                    disabled={true}
                    placeholder='Please select'
                    onItemSelect={() => null}
                    items={questionSettings.options}
                    selectedValue={questionSettings.options?.find(o => o.selectedByDefault)?.id}
                    helperText={questionSettings.helperText}
                    headingText={questionSettings.title}
                />
            case "MultiSelect":
                return <div className='form-field'>
                    <h4>{questionSettings.title}</h4>
                    <ShowHide
                        evaluator={!!questionSettings.helperText}
                        show={<FormHelperText>{questionSettings.helperText}</FormHelperText>}
                    />
                    <div>
                        {questionSettings.options?.map(o => <div key={o.id} className='hierarchy-multi-select-display'>
                            <FormCheckbox disabled={true} checked={o.selectedByDefault} />
                            <span>{o.name}</span>
                        </div>)}
                    </div>
                </div>
            // YesNo begins here
            case "YesNo": {
                const options = [
                    { value: true, label: questionSettings.labelForYes },
                    { value: false, label: questionSettings.labelForNo },
                ];
                return (
                    <div className="check-preview-form-field">
                        <FormRadio
                            options={options}
                            headingText={questionSettings.title}
                            helperText={questionSettings.helperText}
                            onChange={() => { }}
                            selectedValue={questionSettings.defaultValue}
                            disabled={true}
                        />
                    </div>
                );
            }
            case "DefectCapture": {
                const options = [
                    { value: true, label: questionSettings.labelForYes || "Yes" },
                    { value: false, label: questionSettings.labelForNo || "No" },
                ];
                return (
                    <div className="check-preview-form-field">
                        <FormRadio
                            options={options}
                            headingText={questionSettings.title}
                            helperText={questionSettings.helperText}
                            onChange={() => { }}
                            selectedValue={questionSettings.defaultValue}
                            disabled={true}
                        />
                        <div style={{ marginTop: '10px' }}>
                            {renderCameraButton()}
                        </div>
                    </div>
                );
            }
            case "MediaCapture": {
                const maxUploads = questionSettings.maxNumberOfMedia || 0;
                return (
                    <div className="check-preview-form-field">
                        <h4>{questionSettings.title}</h4>
                        <ShowHide
                            evaluator={!!questionSettings.helperText}
                            show={<FormHelperText>{questionSettings.helperText}</FormHelperText>}
                        />
                        <div className="media-capture-container">
                            {renderCameraButton()}
                            <div className="media-capture-upload-count">
                                0/{maxUploads}
                            </div>
                        </div>
                    </div>
                );
            }
            case "AssetLookup":
                return <FormTextInput
                    helperText={questionSettings.helperText}
                    value={''}
                    onChange={() => null}
                    headingText={questionSettings.title}
                    placeholder={questionSettings.placeholder ?? "Search for an asset..."}
                    disabled={true}
                    fill
                ></FormTextInput>
            default:
                return <span>Setting {displayType} not implemented</span>
        }
    }

    const renderActionButtons = () => {
        if (readOnly) {
            return <Tooltip content={inEditMode ? "Close" : "View"}>
                <Button
                    icon={inEditMode ? "cross" : "eye-open"}
                    intent={inEditMode ? Intent.PRIMARY : ""}
                    minimal={!inEditMode}
                    large={false}
                    onClick={inEditMode ? handleSave : handleEdit}
                    loading={loading}
                    disabled={
                        disabled || (inEditMode && (!isValid))
                    }
                    className="spacer-right-small"
                    iconOnly
                />
            </Tooltip>
        }

        const menuItems = [];

        menuItems.push({ key: "duplicate", icon: "duplicate", text: "Duplicate", minimal: true, large: false, onClick: handleClone, disabled: disabled | inEditMode });

        menuItems.push({ key: "add-question", icon: "add", text: "Add Question", minimal: true, large: false, onClick: () => setAddingQuestion(true), disabled: disabled || inEditMode });
        menuItems.push({ key: "add-section", icon: "", text: "Add Section", minimal: true, large: false, onClick: onAddSection, disabled: disabled || inEditMode });
        menuItems.push({ key: "add-page", icon: "", text: "Add Page", minimal: true, large: false, onClick: onAddPage, disabled: disabled || inEditMode });

        return (
            <>
                <ShowHide
                    evaluator={inEditMode}
                    show={<>
                        <Tooltip content="Save">
                            <Button
                                icon="floppy-disk"
                                large={false}
                                onClick={handleSave}
                                loading={loading}
                                disabled={disabled || !isValid || !customSettingIsValid}
                                intent='primary'
                            />
                        </Tooltip>
                        <ShowHide
                            evaluator={!isNew}
                            show={<Tooltip content={isDataDirty ? "Clear" : "Close"}>
                                <Button
                                    icon="cross"
                                    minimal={true}
                                    large={false}
                                    onClick={handleClear}
                                    loading={loading}
                                    disabled={disabled}
                                />
                            </Tooltip>}
                        />
                    </>
                    }
                    hide={
                        <Tooltip content="Edit">
                            <Button
                                icon="edit"
                                minimal={true}
                                large={false}
                                onClick={handleEdit}
                                loading={loading}
                                disabled={disabled}
                            />
                        </Tooltip>
                    }
                />
                <Tooltip content="Delete">
                    <Button
                        icon="trash"
                        minimal={true}
                        large={false}
                        onClick={handleDelete}
                        loading={loading}
                        disabled={disabled || readOnly}
                        className={classNames({ "bp6-intent-danger": !disabled })}
                    />
                </Tooltip>
                <ShowHide
                    evaluator={inEditMode}
                    hide={
                        <Dropdown
                            icon='menu'
                            showArrow={false}
                            loading={loading}
                            items={menuItems.map(i => <MenuItem {...i} />)}
                            className='hierarchy-dropdown-menu'
                        />
                    }
                />
            </>
        );
    }

    return (
        <div className='hierarchy-item-draggable-container'>
            <div ref={setNodeRef} style={draggableStyle} className={classNames("hierarchy-item hierarchy-question", { "active": inEditMode, "disabled": disabled, "being-moved": isItemBeingMoved, "trigger": !!conditionsWhereThisIsTheTrigger.length })}>
                <div className="hierarchy-title-row">
                    <div className="inline-items text-truncate">
                        <ShowHide
                            evaluator={readOnly}
                            hide={<Icon {...listeners} {...attributes} icon="layout-grid" className='drag-handle' />}
                        />
                        <h4 className="hierarchy-title text-truncate">
                            {settings.title ? settings.title : "New Question"}
                        </h4>
                    </div>
                    <div className='inline-items hierarchy-title-text'>
                        <div><span className='hierarchy-title-text-header'>Question:</span> {questionNumber}</div>
                        <div><span className='hierarchy-title-text-header'>Question Type:</span> {selectedQuestionType.name}</div>
                        <ShowHide
                            evaluator={!!conditionsWhereThisIsTheTrigger?.length}
                            show={<div>
                                <Tooltip content={`Used as the trigger for ${conditionsWhereThisIsTheTrigger.length} condition(s)`}>
                                    <span className='hierarchy-title-text-header with-icon'>Trigger <Icon icon="info-sign" size={12} /></span>
                                </Tooltip>
                            </div>}
                        />
                        <ShowHide
                            evaluator={!!settings.condition}
                            show={<div>
                                <Tooltip content={`Conditional based on trigger question ${currentTriggerQuestion?.componentNumber}`}>
                                    <span className='hierarchy-title-text-header with-icon'>Conditional <Icon icon="info-sign" size={12} /></span>
                                </Tooltip>
                            </div>}
                        />

                        <ShowHide
                            evaluator={!!settings.repeatability}
                            show={<div>
                                <Tooltip content={`Repeatable between ${settings.repeatability?.minimumNumberOfRepeats} to ${settings.repeatability?.maximumNumberOfRepeats} times`}>
                                    <span className='hierarchy-title-text-header with-icon'>Repeatable <Icon icon="info-sign" size={12} /></span>
                                </Tooltip>
                            </div>}
                        />
                        <div className='inline-items'>
                            {renderActionButtons()}
                            <div className="hierarchy-title-row-divide"></div>
                            <Button
                                icon={expanded ? "chevron-up" : "chevron-down"}
                                minimal={true}
                                large={false}
                                onClick={handleToggle}
                                loading={loading}
                                className="spacer-right-small"
                            />
                        </div>
                    </div>
                </div>
                <ShowHide
                    evaluator={expanded}
                    show={
                        <div className='section-content'>
                            <ShowHide
                                evaluator={inEditMode}
                                show={
                                    <>
                                        <ShowHide
                                            evaluator={readOnly}
                                            show={<>
                                                <FormReadonly
                                                    value={questionSettings.title ?? "Untitled"}
                                                    headingText='Question header'
                                                />
                                                <FormReadonly
                                                    value={questionSettings.helperText ?? "-"}
                                                    headingText='Question helper text'
                                                />
                                                {draftHelperImages && (
                                                    <MediaList loading={loading || disabled} maxNoOfMedia={3} media={[...initialHelperImages]} mediaTitle="Question helper Images" />)
                                                }
                                            </>
                                            }

                                            hide={<>
                                                <FormTextInput
                                                    fill
                                                    value={questionSettings.title ?? ""}
                                                    onChange={onTitleChange}
                                                    headingText='Question header'
                                                    placeholder='Please type the header here'
                                                    dangerHelperText={errors.title}
                                                />
                                                <FormTextInput
                                                    fill
                                                    value={questionSettings.helperText ?? ""}
                                                    onChange={onHelperTextChange}
                                                    headingText='Question helper text'
                                                    placeholder='Please type the helper text here'
                                                />
                                                {draftHelperImages && (
                                                    <MediaList loading={loading || disabled} maxNoOfMedia={3} media={[...initialHelperImages, ...draftHelperImages]} allowAddMedia={!readOnly} handleAddMedia={handleAddMedia} allowDeleteMedia={!readOnly} handleDeleteMedia={handleDeleteMedia} mediaTitle="Question helper Images" />)
                                                }
                                            </>}
                                        />
                                        <h3 style={{ marginTop: '20px' }}>Question Settings</h3>
                                        <GridLayout numberOfColumns={4} customColumnRatios={QuestionTypesConfiguration[questionSettings.$componentType]?.fields?.length === 1 ? [1] : [1, 1, 1, 1]} className='hierarchy-question-grid'>
                                            {renderQuestionSettings()}
                                        </GridLayout>

                                        <h3>Question Options</h3>
                                        <div className='hierarchy-question-options'>
                                            <GridLayout numberOfColumns={3} customColumnRatios={[1, 1, 2]} className='hierarchy-question-grid'>
                                                <div>
                                                    <FormSwitch
                                                        headingText='Required'
                                                        checked={questionSettings.required}
                                                        onChange={handleRequiredToggle}
                                                        disabled={readOnly}
                                                    />
                                                </div>
                                                <div>
                                                    <Tooltip content={reasonWhyComponentCannotBeRepeatable} position="right">
                                                        <FormSwitch
                                                            checked={!!questionSettings.repeatability}
                                                            onChange={handleRepeatableToggle}
                                                            disabled={!!reasonWhyComponentCannotBeRepeatable || readOnly || !selectedQuestionType?.canBeRepeated}
                                                            headingText="Repeatable"
                                                            labelElement={questionSettings.repeatability ? `Min ${questionSettings.repeatability.minimumNumberOfRepeats}, Max ${questionSettings.repeatability.maximumNumberOfRepeats}` : ''}
                                                        />
                                                    </Tooltip>
                                                </div>
                                                <div>
                                                    <Tooltip content={conditionalHelperText} position="right">
                                                        <FormSwitch
                                                            checked={!!questionSettings.condition}
                                                            onChange={handleConditionalToggle}
                                                            disabled={!pottentialTriggerQuestions.length || readOnly}
                                                            headingText="Conditional"
                                                            labelElement={triggerQuestion ? `Trigger question ${triggerQuestion.componentNumber}, Answers: ${triggerAnswers}` : ''}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </GridLayout>
                                        </div>
                                        <HierarchyRepeatableModal
                                            isOpen={settingRepeatability}
                                            component={questionSettings}
                                            isSection={false}
                                            onCancel={() => setSettingRepeatability(false)}
                                            onSave={(newRepeatability) => onRepeatabilityChange(newRepeatability)}
                                        />
                                        <HierarchyConditionalModal
                                            isOpen={settingConditionality}
                                            component={questionSettings}
                                            isSection={false}
                                            onCancel={() => setSettingConditionality(false)}
                                            onSave={(newCondition) => onConditionalityChange(newCondition)}
                                            pottentialTriggerQuestions={pottentialTriggerQuestions}
                                        />
                                    </>
                                }
                                hide={
                                    <div className="hierarchy-question-display">
                                        {renderQuestionDisplay()}
                                    </div>
                                }
                            />
                        </div>
                    }
                />
            </div>

            <HierarchyAddQuestionModal
                isOpen={addingQuestion}
                onCancel={() => setAddingQuestion(false)}
                onSave={(questionType) => {
                    onAddQuestion(questionType);
                    setAddingQuestion(false);
                }}
                questionTypes={questionTypes}
            />

            <AlertConfirm
                title="Clear changes"
                isOpen={showClearSettingsWarning}
                onConfirm={onClearConfirmed}
                onCancel={() => setShowClearSettingsWarning(false)}
                confirmButtonIntent="danger"
                confirmButtonText="Clear changes"
            >
                <p>Are you sure you want to clear your changes? Any changes made to the question will be removed.</p>
            </AlertConfirm>
        </div>
    );
}

HierarchyQuestion.propTypes = {
    questionNumber: PropTypes.number.isRequired,
    settings: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onClone: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onAddMedia: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    questionTypes: PropTypes.array.isRequired,
    inEditMode: PropTypes.bool.isRequired,
    reasonWhyComponentCannotBeRepeatable: PropTypes.string.isRequired,
    pottentialTriggerQuestions: PropTypes.array.isRequired,
    currentTriggerQuestion: PropTypes.object,
    helperImages: PropTypes.array,
    conditionalHelperText: PropTypes.node,
    conditionsWhereThisIsTheTrigger: PropTypes.array,
    readOnly: PropTypes.bool.isRequired,
    onAddQuestion: PropTypes.func.isRequired,
    onAddSection: PropTypes.func.isRequired,
    onAddPage: PropTypes.func.isRequired
};